// Opens a document whose URL first has to be fetched from the backend (e.g. a presigned S3 link)
// in a new tab, without triggering the browser's pop-up blocker.
//
// Why: browsers only let a page open a new tab while it is still handling the user's click. If we
// wait for the backend and only then call window.open(url), the click has "expired" and every
// browser treats it as an unrequested pop-up (blocked, or "allow pop-ups?" prompt).
//
// How: open an empty tab synchronously, inside the click (always allowed), show a short loading
// message in it, then send that tab to the URL once it arrives. On failure the tab is closed.
// The default pop-up settings of every major browser allow this. Only if the user (or an
// extension) blocks ALL new tabs does window.open return null — then a toast explains it. We never
// fall back to the current tab: that would throw away unsaved work (e.g. a half-filled form).
//
// IMPORTANT: call this directly from the click handler, BEFORE any `await`.
//
//   onClick={() => openUrlInNewTab(() => fetchPresignedUrl(id)).catch(() => {})}
//
// `getUrl` returns (a promise of) the URL. Resolves with the URL; rejects with the fetch error
// (or "No URL" / "POPUP_BLOCKED") after closing the tab, so callers can keep their own handling.

import { toast } from "react-toastify"

const LOADING_TITLE = "Opening document..."

const showLoading = (tab) => {
    try {
        const doc = tab.document
        doc.title = LOADING_TITLE
        const body = doc.body || doc.appendChild(doc.createElement("body"))
        body.style.cssText = "margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#4b5563;background:#f9fafb;"
        body.textContent = "Opening document, please wait..."
    }
    catch {
        // Cosmetic only — the tab still works without the message.
    }
}

const openUrlInNewTab = async (getUrl) => {
    const tab = window.open("", "_blank")
    if (!tab) {
        toast.error("Your browser blocked opening a new tab. Please allow new tabs (pop-ups) for this site and try again.")
        throw new Error("POPUP_BLOCKED")
    }
    // Same protection as "noopener": the opened page cannot reach back into this app.
    try { tab.opener = null } catch { /* ignore */ }
    showLoading(tab)

    try {
        const url = await getUrl()
        if (!url) throw new Error("No URL")
        // If the user already closed the loading tab themselves, there is nothing to do.
        if (!tab.closed) tab.location.replace(url)
        return url
    }
    catch (e) {
        try { tab.close() } catch { /* ignore */ }
        throw e
    }
}

export default openUrlInNewTab
